import { alamatUlasanPendek } from '../utils/ulasanPendek';
import { useStatusUlasan } from './UlasanGoogleAlat';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Download as DownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import axios from 'axios';
import useSWR from 'swr';
import { apiHost } from '../utils/apiHost';
import {
  UKURAN_KARTU, WARNA_AKSEN, MIN_CM, MAKS_CM, ukuranDariPilihan, bangunSvgKartu, htmlKartu, namaBerkasKartu,
} from '../struk/kartuUlasan';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
const fetcher = (url) => api.get(url).then((r) => r.data);

/** Logo (alamat gambar) -> data URL supaya ikut tersimpan di SVG/PNG. Gagal = kosong (kartu tanpa logo). */
export const muatLogoDataUrl = async (url) => {
  try {
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) return '';
    const blob = await r.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve('');
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    return '';
  }
};

/** SVG -> PNG (data blob) beresolusi cetak (300 dpi). */
export const svgKePng = (svg, lebarMm, tinggiMm, dpi = 300) => new Promise((resolve, reject) => {
  // Kartu besar: resolusi diturunkan agar sisi terpanjang tidak melebihi 4000 px (memori aman di HP dan browser).
  const dpiPakai = Math.min(dpi, (4000 * 25.4) / Math.max(lebarMm, tinggiMm));
  const px = (mm) => Math.max(1, Math.round((mm / 25.4) * dpiPakai));
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = px(lebarMm);
    canvas.height = px(tinggiMm);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Gagal membuat PNG'))), 'image/png');
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Gagal membuat gambar kartu')); };
  img.src = url;
});

/** Cetak lewat jendela cetak browser (iframe tersembunyi), satu halaman seukuran kartu. */
const cetakHtml = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.top = '-10000px';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 400);
};

/**
 * Halaman Kartu QR Ulasan: kartu siap cetak per cabang (logo cabang, ajakan ulasan, bintang, QR). Ukuran pilihan atau ukuran sendiri.
 * Tautan ulasan diisi di menu Struk > Ulasan Google.
 */
export const KartuUlasan = ({ cabangUuid: cabangProp = null, onIsiTautan = null } = {}) => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isSuperAdmin = user?.role === 'superadmin';
  const { bolehAtur } = useStatusUlasan(); // hanya superadmin yang mengisi tautan ulasan
  const cabangSendiri = user?.cabang?.uuid || user?.cabanguuid || '';
  // Di dalam halaman Ulasan Google cabang ditentukan induknya (tanpa judul & pilihan cabang sendiri).
  const tertanam = cabangProp !== null;
  const cabangUuid = tertanam ? cabangProp : (isSuperAdmin ? (params.get('cabang') || '') : cabangSendiri);

  const { data: daftarCabang } = useSWR(isSuperAdmin ? '/cabang' : null, fetcher);
  const { data: konf } = useSWR(cabangUuid ? `/getkonfigurasicabang?cabangUuid=${cabangUuid}` : null,
    (url) => api.get(url).then((r) => r.data).catch((e) => (e.response?.status === 404 ? { data: null } : Promise.reject(e))));
  const { data: cabangData } = useSWR(cabangUuid ? `/cabang/${cabangUuid}` : null, fetcher);

  const konfigurasi = konf?.data || null;
  const namaCabang = cabangData?.data?.namacabang || '';
  const urlUlasan = alamatUlasanPendek(konfigurasi); // QR memuat alamat pendek milik kita, bukan tautan Google
  const logoUrl = konfigurasi?.logo ? `${getApiBaseUrl()}/uploads/${konfigurasi.logo}` : '';

  const [ukuran, setUkuran] = useState('kotak10');
  const [lebarCm, setLebarCm] = useState('10');
  const [tinggiCm, setTinggiCm] = useState('10');
  const [aksen, setAksen] = useState(WARNA_AKSEN[0].kunci); // bawaan: bingkai empat warna Google
  const [pakaiLogo, setPakaiLogo] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [galat, setGalat] = useState('');

  useEffect(() => {
    let batal = false;
    setLogoDataUrl('');
    if (logoUrl) muatLogoDataUrl(logoUrl).then((d) => { if (!batal) setLogoDataUrl(d); });
    return () => { batal = true; };
  }, [logoUrl]);

  const { lebar, tinggi } = ukuranDariPilihan(ukuran, lebarCm, tinggiCm);
  const svg = useMemo(() => (urlUlasan
    ? bangunSvgKartu({ lebar, tinggi, namaCabang, logoDataUrl: pakaiLogo ? logoDataUrl : '', urlUlasan, aksen })
    : ''), [lebar, tinggi, namaCabang, logoDataUrl, pakaiLogo, urlUlasan, aksen]);

  const cetak = () => { setGalat(''); cetakHtml(htmlKartu(svg, lebar, tinggi)); };
  const unduhPng = async () => {
    setGalat('');
    try {
      const blob = await svgKePng(svg, lebar, tinggi);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = namaBerkasKartu(namaCabang, 'png');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setGalat(e.message || 'Gagal membuat PNG');
    }
  };

  const sisiCustomSalah = ukuran === 'custom' && [lebarCm, tinggiCm].some((v) => {
    const n = Number(String(v).replace(',', '.'));
    return !Number.isFinite(n) || n < MIN_CM || n > MAKS_CM;
  });

  return (
    <Box sx={tertanam ? {} : { p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
      {!tertanam && <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Kartu QR Ulasan Google</Typography>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kartu siap cetak untuk standing acrylic, stiker, atau kartu di meja kasir. Pelanggan memindai QR dan langsung masuk ke halaman tulis ulasan Google cabang ini.
      </Typography>

      {isSuperAdmin && !tertanam && (
        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
          <InputLabel id="label-cabang-kartu">Cabang</InputLabel>
          <Select labelId="label-cabang-kartu" label="Cabang" value={cabangUuid} onChange={(e) => setParams(e.target.value ? { cabang: e.target.value } : {})}>
            {(daftarCabang?.data || daftarCabang || []).map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {!cabangUuid && <Alert severity="info">Pilih cabang dulu.</Alert>}

      {cabangUuid && !urlUlasan && konf && (
        <Alert
          severity="warning"
          action={bolehAtur ? <Button color="inherit" size="small" onClick={() => (onIsiTautan ? onIsiTautan() : navigate('/ulasangoogle'))}>Isi tautan</Button> : null}
        >
          {bolehAtur
            ? 'Tautan ulasan Google cabang ini belum diisi. Isi dulu di tab Tautan ulasan.'
            : 'Tautan ulasan Google cabang ini belum diisi. Minta superadmin mengisinya.'}
        </Alert>
      )}

      {cabangUuid && urlUlasan && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
          <Card sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
            <CardContent>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="label-ukuran-kartu">Ukuran kartu</InputLabel>
                  <Select labelId="label-ukuran-kartu" label="Ukuran kartu" value={ukuran} onChange={(e) => setUkuran(e.target.value)}>
                    {UKURAN_KARTU.map((u) => <MenuItem key={u.kunci} value={u.kunci}>{u.nama}</MenuItem>)}
                  </Select>
                </FormControl>
                {ukuran === 'custom' && (
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" label="Lebar (cm)" value={lebarCm} onChange={(e) => setLebarCm(e.target.value)} error={sisiCustomSalah} inputProps={{ inputMode: 'decimal' }} />
                    <TextField size="small" label="Tinggi (cm)" value={tinggiCm} onChange={(e) => setTinggiCm(e.target.value)} error={sisiCustomSalah} inputProps={{ inputMode: 'decimal' }}
                      helperText={sisiCustomSalah ? `${MIN_CM}-${MAKS_CM} cm` : ''} />
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary">Ukuran cetak: {(lebar / 10).toLocaleString('id-ID')} × {(tinggi / 10).toLocaleString('id-ID')} cm</Typography>

                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Warna bingkai</Typography>
                  <Stack direction="row" spacing={1}>
                    {WARNA_AKSEN.map((w) => (
                      <Box
                        key={w.kunci}
                        role="button"
                        aria-label={`Warna ${w.nama}`}
                        onClick={() => setAksen(w.kunci)}
                        sx={{ width: 28, height: 28, borderRadius: '50%', background: w.latar || w.kunci, cursor: 'pointer', border: aksen === w.kunci ? '3px solid #000' : '2px solid #fff', boxShadow: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>

                <FormControlLabel
                  control={<Switch checked={pakaiLogo} onChange={(e) => setPakaiLogo(e.target.checked)} disabled={!logoUrl} />}
                  label={logoUrl ? 'Tampilkan logo cabang' : 'Logo cabang belum diunggah'}
                />

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" startIcon={<PrintIcon />} onClick={cetak} disabled={sisiCustomSalah}>Cetak / PDF</Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={unduhPng} disabled={sisiCustomSalah}>Unduh PNG</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Di jendela cetak, pilih printer atau &quot;Simpan sebagai PDF&quot;. Atur skala 100% dan matikan header/footer. Untuk percetakan, kirim PNG (300 dpi).
                </Typography>
                {galat && <Alert severity="error">{galat}</Alert>}
              </Stack>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box
              data-testid="pratinjau-kartu"
              sx={{ width: '100%', maxWidth: 460, boxShadow: 3, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', lineHeight: 0, '& svg': { width: '100%', height: 'auto' } }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default KartuUlasan;
